// The rule that a day reported as Time Off has to say why.
//
// Kept out of the component so it can be driven directly by
// `scripts/verify-time-off-work-report.mjs`, and so the modal opened from an ordinary Request and
// the one opened from a sub-task card cannot answer the question differently — they are the same
// modal, and this is the same rule.
//
// Time Off is not another report type with another label. It carries no duration, and on the server
// it subtracts a day of capacity from the reporter's team occupancy rather than adding work to it.
// A blank one would move somebody's occupancy with nothing written on it.
//
// The modal is only the first of three doors: the same entry is reachable from the API and from the
// update endpoint, both of which enforce this server-side. What lives here is the part that stops
// somebody submitting a form they would only be refused for.

// The exact sentence the product requires, and the exact sentence the API returns — the server's
// `TimeOffWorkReportGuard::MESSAGE`. One constant, one wording: a person refused by the API must
// read what the form would have said.
export const TIME_OFF_WORK_REPORT_COMMENT_MESSAGE =
    'Reporting Time Off requires an explanation note in the Comment box before submission.';

// The wire value the server validates against, as a plain string so this module stays free of
// runtime imports and can be transpiled and driven on its own. `WorkReportType.TIME_OFF` holds the
// same value, and the harness pins the two together.
export const TIME_OFF_WORK_REPORT_TYPE = 'time_off';

// Whether the selected report type is Time Off.
export const isTimeOffWorkReportType = (type: string | null): boolean => type === TIME_OFF_WORK_REPORT_TYPE;

// Whether a Time Off report is still missing its explanation.
//
// Whitespace is empty. A single space satisfies "not an empty string" while explaining nothing at
// all, and it is the cheapest possible way past this rule.
export const isTimeOffExplanationMissing = (type: string | null, comment: string): boolean =>
    isTimeOffWorkReportType(type) && comment.trim() === '';
